/**
 * Read Piece Page (/read/:postId)
 * Where a picked author adds notes to their lines (or declines), and where
 * anyone can revisit a piece after its Read day.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthenticationContext';
import ReadPieceView from '../components/read/ReadPieceView';

const formatDay = (key) => new Date(`${key}T12:00:00Z`).toLocaleDateString('en-GB', {
  weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC'
});

const ReadPiecePage = () => {
  const { postId } = useParams();
  const { token, loading: authLoading } = useAuth();
  const [piece, setPiece] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | missing
  const [declining, setDeclining] = useState(false);

  const load = useCallback(() => {
    axios.get(`/api/read/post/${postId}`)
      .then((res) => { setPiece(res.data); setStatus('ok'); })
      .catch(() => setStatus('missing'));
  }, [postId]);

  // Wait for auth so the author gets their annotate view, not the public 404.
  useEffect(() => { if (!authLoading) load(); }, [authLoading, token, load]);

  const decline = async () => {
    if (!window.confirm("Decline this pick? Your piece won't be featured in Read.")) return;
    setDeclining(true);
    try {
      await axios.post(`/api/read/post/${postId}/decline`);
      load();
    } finally {
      setDeclining(false);
    }
  };

  if (status === 'loading') return <div className="container loading">Loading...</div>;
  if (status === 'missing') {
    return (
      <div className="container">
        <h1>Read</h1>
        <div className="no-posts">
          <p>
            This piece isn't available.{' '}
            {!token && <><Link to="/login">Log in</Link> if it's yours. </>}
            <Link to="/?tab=read">See today's read</Link>
          </p>
        </div>
      </div>
    );
  }

  const canAnnotate = piece.isAuthor && piece.status !== 'declined';
  let authorText = null;
  if (piece.isAuthor) {
    if (piece.status === 'declined') authorText = "You declined this pick. It won't be featured.";
    else if (piece.shownOn) authorText = `Your piece was the Read on ${formatDay(piece.shownOn)}. You can still edit your notes.`;
    else if (piece.status === 'ready') authorText = `You're all set. Your piece goes out on ${formatDay(piece.showOn)}, or the next free day if that one has passed. You can keep editing your notes until then.`;
    else authorText = `Your piece was picked for the Read on ${formatDay(piece.showOn)}. Tap the lines you're proudest of and add a short note on why they work. It goes out once it has at least one note.`;
  }

  return (
    <div className="container">
      <h1>Read</h1>
      {piece.isAuthor && (
        <div className="read-author-box">
          <p>{authorText}</p>
          <button
            type="button"
            className="read-btn"
            onClick={decline}
            disabled={declining || piece.status === 'declined' || !!piece.shownOn}
          >
            Decline this pick
          </button>
        </div>
      )}
      <ReadPieceView piece={piece} annotate={canAnnotate} onChange={setPiece} />
    </div>
  );
};

export default ReadPiecePage;
